import React, { useState, useEffect } from 'react';
import {
  Box, Typography, FormControl, FormControlLabel,
  RadioGroup, Radio, TextField, Select, MenuItem,
  InputLabel, Button, Alert, Collapse, CircularProgress
} from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import BusinessIcon from '@mui/icons-material/Business';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import api from '../services/api';

// 常用愛心碼
const DEFAULT_LOVE_CODES = [
  { code: '168001', name: '創世基金會' },
  { code: '8585', name: '伊甸基金會' },
  { code: '25885', name: '家扶基金會' },
  { code: '8001', name: '兒福聯盟' },
  { code: '7789', name: '陽光基金會' },
  { code: '510', name: '流浪動物之家' },
  { code: '888', name: '喜憨兒基金會' },
  { code: '5014', name: '世界展望會' }
];

// 載具類型
const CARRIER_TYPES = [
  { code: 'EJ0113', name: '速買配載具 (Email 歸戶)' },
  { code: '3J0002', name: '手機條碼' },
  { code: 'CQ0001', name: '自然人憑證' }
];

const EInvoiceSelector = ({ value, onChange }) => {
  const [invoiceType, setInvoiceType] = useState(value?.type || 'carrier');
  const [carrierType, setCarrierType] = useState(value?.carrierType || 'EJ0113');
  const [carrierId, setCarrierId] = useState(value?.carrierId || '');
  const [buyerId, setBuyerId] = useState(value?.buyerId || '');
  const [companyName, setCompanyName] = useState(value?.companyName || '');
  const [loveCode, setLoveCode] = useState(value?.loveCode || '168001');
  const [customLoveCode, setCustomLoveCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [loveCodes, setLoveCodes] = useState(DEFAULT_LOVE_CODES);

  // 取得愛心碼列表
  useEffect(() => {
    const fetchLoveCodes = async () => {
      try {
        const response = await api.get('/einvoice/lovecodes');
        if (response.data.success && response.data.loveCodes) {
          setLoveCodes(response.data.loveCodes);
        }
      } catch (err) {
        // 使用預設值
      }
    };
    fetchLoveCodes();
  }, []);

  // 當任何值變更時，呼叫 onChange
  useEffect(() => {
    const data = {
      type: invoiceType,
      carrierType: invoiceType === 'carrier' ? carrierType : null,
      carrierId: invoiceType === 'carrier' && carrierType !== 'EJ0113' ? carrierId : null,
      buyerId: invoiceType === 'company' ? buyerId : null,
      companyName: invoiceType === 'company' ? companyName : null,
      loveCode: invoiceType === 'donate' ? (loveCode === 'custom' ? customLoveCode : loveCode) : null,
      donate: invoiceType === 'donate'
    };
    onChange(data);
  }, [invoiceType, carrierType, carrierId, buyerId, companyName, loveCode, customLoveCode, onChange]);

  // 驗證載具
  const verifyCarrier = async () => {
    if (!carrierId) {
      setVerifyResult({ success: false, message: '請輸入載具號碼' });
      return;
    }

    setVerifying(true);
    setVerifyResult(null);

    try {
      const response = await api.post('/einvoice/verify-carrier', {
        carrier_type: carrierType,
        carrier_id: carrierId
      });

      if (response.data.success) {
        if (response.data.valid) {
          setVerifyResult({ success: true, message: '載具驗證成功' });
        } else {
          setVerifyResult({ success: false, message: response.data.message || '載具驗證失敗' });
        }
      } else {
        setVerifyResult({ success: false, message: response.data.error || '驗證失敗' });
      }
    } catch (err) {
      setVerifyResult({ success: false, message: '驗證服務暫時無法使用' });
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2, mt: 2 }}>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <ReceiptIcon color="primary" />
        電子發票
      </Typography>

      <FormControl component="fieldset" fullWidth>
        <RadioGroup
          value={invoiceType}
          onChange={(e) => {
            setInvoiceType(e.target.value);
            setVerifyResult(null);
          }}
        >
          {/* 載具 */}
          <FormControlLabel
            value="carrier"
            control={<Radio />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PhoneIphoneIcon fontSize="small" />
                <span>載具歸戶 (推薦)</span>
              </Box>
            }
          />
          <Collapse in={invoiceType === 'carrier'}>
            <Box sx={{ pl: 4, pr: 2, pb: 2 }}>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>載具類型</InputLabel>
                <Select
                  value={carrierType}
                  label="載具類型"
                  onChange={(e) => {
                    setCarrierType(e.target.value);
                    setVerifyResult(null);
                  }}
                >
                  {CARRIER_TYPES.map(type => (
                    <MenuItem key={type.code} value={type.code}>{type.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {carrierType !== 'EJ0113' && (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <TextField
                    fullWidth
                    size="small"
                    label={carrierType === '3J0002' ? '手機條碼 (以 / 開頭)' : '自然人憑證條碼'}
                    placeholder={carrierType === '3J0002' ? '/XXXXXXX' : 'XX00000000000000'}
                    value={carrierId}
                    onChange={(e) => {
                      setCarrierId(e.target.value.toUpperCase());
                      setVerifyResult(null);
                    }}
                    error={verifyResult && !verifyResult.success}
                    helperText={verifyResult?.message}
                  />
                  <Button
                    variant="outlined"
                    onClick={verifyCarrier}
                    disabled={verifying}
                    sx={{ minWidth: 80, height: 40 }}
                  >
                    {verifying ? <CircularProgress size={20} /> : '驗證'}
                  </Button>
                </Box>
              )}

              {carrierType === 'EJ0113' && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  發票將存入您的 Email，可透過速買配載具查詢
                </Alert>
              )}
            </Box>
          </Collapse>

          {/* 捐贈 */}
          <FormControlLabel
            value="donate"
            control={<Radio />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <VolunteerActivismIcon fontSize="small" color="error" />
                <span>捐贈發票給公益團體</span>
              </Box>
            }
          />
          <Collapse in={invoiceType === 'donate'}>
            <Box sx={{ pl: 4, pr: 2, pb: 2 }}>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>選擇愛心碼</InputLabel>
                <Select
                  value={loveCode}
                  label="選擇愛心碼"
                  onChange={(e) => setLoveCode(e.target.value)}
                >
                  {loveCodes.map(item => (
                    <MenuItem key={item.code} value={item.code}>
                      {item.name} ({item.code})
                    </MenuItem>
                  ))}
                  <MenuItem value="custom">其他愛心碼...</MenuItem>
                </Select>
              </FormControl>

              {loveCode === 'custom' && (
                <TextField
                  fullWidth
                  size="small"
                  label="自訂愛心碼"
                  placeholder="請輸入愛心碼"
                  value={customLoveCode}
                  onChange={(e) => setCustomLoveCode(e.target.value)}
                />
              )}
            </Box>
          </Collapse>

          {/* 公司戶 */}
          <FormControlLabel
            value="company"
            control={<Radio />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BusinessIcon fontSize="small" />
                <span>公司戶 (打統編)</span>
              </Box>
            }
          />
          <Collapse in={invoiceType === 'company'}>
            <Box sx={{ pl: 4, pr: 2, pb: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="統一編號"
                placeholder="請輸入 8 位數統一編號"
                value={buyerId}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 8);
                  setBuyerId(val);
                }}
                sx={{ mb: 2 }}
                inputProps={{ maxLength: 8 }}
              />
              <TextField
                fullWidth
                size="small"
                label="公司名稱 (選填)"
                placeholder="請輸入公司名稱"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
              />
            </Box>
          </Collapse>
        </RadioGroup>
      </FormControl>
    </Box>
  );
};

export default EInvoiceSelector;
